import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Logo from '../../common/Logo';

const { FiArrowRight, FiCheckCircle, FiMenu, FiX } = FiIcons;

const TRUST = [
  'For asset-heavy teams still running on spreadsheets and email',
  'JSM-based asset tracking + internal helpdesk — live in 4–6 weeks',
  'Ongoing retainer so your system stays clean and current',
];

const NAV_LINKS = [
  { label: 'Problem', id: 'problem' },
  { label: 'How It Works', id: 'how-it-works' },
  { label: 'Service', id: 'service' },
  { label: 'Pricing', id: 'pricing' },
  { label: 'FAQ', id: 'faq' },
];

const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

const LogoButton = ({ className = '' }) => (
  <button
    onClick={scrollTop}
    className={`flex items-center gap-2 bg-transparent border-none p-0 cursor-pointer ${className}`}
    aria-label="Back to top"
  >
    <Logo className="w-8 h-8" />
    <span className="text-white font-bold text-lg tracking-tight">Valtivo</span>
  </button>
);

const MobileMenu = ({ open, onClose, scrollTo }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} />
        <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
          transition={{ type: 'tween', duration: 0.25 }}
          className="fixed top-0 right-0 bottom-0 z-50 w-72 flex flex-col" style={{ background: '#052e16' }}>
          <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: 'rgba(74,222,128,0.2)' }}>
            <button onClick={() => { scrollTop(); onClose(); }} className="flex items-center gap-2 bg-transparent border-none p-0 cursor-pointer">
              <Logo className="w-7 h-7" />
              <span className="text-white font-bold text-base">Valtivo</span>
            </button>
            <button onClick={onClose} style={{ color: '#4ade80' }}>
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex flex-col px-4 gap-1 flex-1 pt-4">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => { scrollTo(l.id); onClose(); }}
                className="text-left px-4 py-3 rounded-lg text-base font-medium" style={{ color: '#bbf7d0' }}>
                {l.label}
              </button>
            ))}
          </nav>
          <div className="px-6 pb-8">
            <button onClick={() => { scrollTo('booking'); onClose(); }}
              className="w-full py-3.5 rounded font-bold text-base" style={{ background: '#16a34a', color: '#ffffff' }}>
              Book a Discovery Call
            </button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const StickyNav = ({ onMenuOpen, scrollTo }) => {
  const [visible, setVisible] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setVisible(y < lastY.current && y > 80);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.div initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -80, opacity: 0 }}
          transition={{ duration: 0.25 }} className="fixed top-0 left-0 right-0 z-50"
          style={{ background: 'rgba(5,46,22,0.97)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(74,222,128,0.15)' }}>
          <div className="flex items-center justify-between px-6 lg:px-16 py-3 max-w-7xl mx-auto">
            <button onClick={scrollTop} className="flex items-center gap-2 bg-transparent border-none p-0 cursor-pointer">
              <Logo className="w-7 h-7" />
              <span className="text-white font-bold text-base">Valtivo</span>
            </button>
            <div className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map((l) => (
                <button key={l.id} onClick={() => scrollTo(l.id)} className="text-sm font-medium" style={{ color: '#bbf7d0' }}>{l.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => scrollTo('booking')} className="hidden md:block px-4 py-2 rounded text-sm font-bold" style={{ background: '#16a34a', color: '#ffffff' }}>
                Book a Call
              </button>
              <button onClick={onMenuOpen} className="md:hidden p-2" style={{ color: '#4ade80' }}>
                <SafeIcon icon={FiMenu} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const HomeHero = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <>
      <StickyNav onMenuOpen={() => setMenuOpen(true)} scrollTo={scrollTo} />
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} scrollTo={scrollTo} />
      <section style={{ background: '#052e16', minHeight: '100vh' }} className="relative flex flex-col overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.2) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.2) 1px,transparent 1px)', backgroundSize: '48px 48px' }} />
        <nav className="flex items-center justify-between px-6 lg:px-16 py-5 relative z-20">
          <LogoButton />
          <div className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-sm font-medium transition-colors" style={{ color: '#bbf7d0' }}>{l.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => scrollTo('booking')} className="hidden md:block px-5 py-2.5 rounded text-sm font-bold" style={{ background: '#16a34a', color: '#ffffff' }}>
              Book a Discovery Call
            </button>
            <button onClick={() => setMenuOpen(true)} className="md:hidden p-2" style={{ color: '#4ade80' }}>
              <SafeIcon icon={FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </nav>
        <div className="flex-1 flex items-center max-w-6xl mx-auto px-6 lg:px-16 pt-16 pb-36 relative z-10">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-widest mb-8"
                style={{ background: 'rgba(74,222,128,0.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.25)' }}>
                JSM for Asset-Heavy Operations · 50–300 Employees
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 text-white">
                Stop tracking assets and support tickets in spreadsheets.
              </h1>
              <p className="text-lg md:text-xl leading-relaxed mb-10" style={{ color: '#bbf7d0' }}>
                We build JSM-based asset management and internal helpdesk systems for operations and IT managers at medical device, equipment rental, field service, and logistics companies. Live in 4–6 weeks. Retainer-backed so it stays current.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-10">
                <button onClick={() => scrollTo('booking')} className="group flex items-center justify-center gap-2 px-7 py-4 rounded font-bold text-base" style={{ background: '#16a34a', color: '#ffffff' }}>
                  Book a Discovery Call
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={() => scrollTo('how-it-works')} className="px-7 py-4 rounded font-bold text-base"
                  style={{ background: 'rgba(255,255,255,0.07)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.15)' }}>
                  See How It Works
                </button>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                {TRUST.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4 flex-shrink-0" style={{ color: '#4ade80' }} />
                    <span className="text-sm font-medium" style={{ color: '#bbf7d0' }}>{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width: '100%', height: '120px' }}>
            <path d="M0 120L1440 120L1440 70C1200 120 960 20 720 45C480 70 240 120 0 70L0 120Z" fill="#f0fdf4" />
          </svg>
        </div>
      </section>
    </>
  );
};

export default HomeHero;